import React from 'react'
import { useRouter } from 'next/router'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'

import { Login, Signup } from 'config/constants'
import { CaptchaType, useRetrievePasswordMutation } from 'types/domain'
import { graphqlErrorCatch } from 'utils'
import { AnchorLink } from 'components/ui'
import { PasswordResetFormProvider, PasswordResetFormValues, usePasswordResetForm } from '../formContext'
import {
  AuthenticationFormValues,
  CaptchaCodeSendAndInput,
  IdentifierInput,
  PasswordInputStep,
  useFormValidations,
} from 'components/business'
import { Button, Stack, Title } from '@mantine/core'
import { UseFormReturnType } from '@mantine/form/lib/types'
import { SignupFormValues } from 'modules/Signup/formContext'
import Footer from 'modules/Signup/Form/Footer'
import useCachedIdentifier from 'components/business/hooks/useCachedIdentifier'

const ResetForm = () => {
  const router = useRouter()
  const { t } = useTranslation('pagePwdReset')
  const { resetFormValidate } = useFormValidations()

  const form = usePasswordResetForm({
    initialValues: { identifier: '', password: '', confirmPassword: '', captcha: '' },
    validateInputOnBlur: ['identifier'],
    validate: (values) => {
      return resetFormValidate(values, form)
    },
  })

  useCachedIdentifier(form)

  const submitButtonDisabled =
    !form.values.identifier ||
    !form.isValid('identifier') ||
    !form.values.password ||
    !form.values.captcha ||
    !form.values.confirmPassword

  const [retrievePasswordMutation, { loading: submitting }] = useRetrievePasswordMutation()

  const handlePasswordReset = async (values: PasswordResetFormValues) => {
    const { hasErrors } = form.validate()
    if (hasErrors) {
      return
    }
    const { identifier, password, captcha } = values
    try {
      const { data } = await retrievePasswordMutation({
        variables: {
          input: {
            identifier: identifier.trim(),
            password,
            captcha,
          },
        },
      })
      if (data?.retrievePassword) {
        await router.replace(Login)
      }
    } catch (err: any) {
      const { graphQLErrors } = err
      graphqlErrorCatch({
        graphQLErrors,
        mapping: new Map([['error.mismatched', t('user:authentication.form.errors.captchaMismatched')]]),
        defaultMessage: t('errors.default'),
      })
    }
  }

  const identifierNotExistedErrorHandler = () => {
    const { identifier } = form.values
    const message = (
      <Trans
        i18nKey="pagePwdReset:errors.identifierNotExisted"
        components={{
          component: <AnchorLink href={Signup} />,
        }}
      />
    )
    form.setFieldError('identifier', message)
  }

  return (
    <PasswordResetFormProvider form={form}>
      <form className="h-full">
        <Stack spacing={'2xl' as any}>
          <Title order={2}>{t('title')}</Title>
          <Stack spacing="xl">
            <IdentifierInput
              size="md"
              required
              formContext={form as UseFormReturnType<AuthenticationFormValues | SignupFormValues>}
            />
            <CaptchaCodeSendAndInput
              captchaType={CaptchaType.RetrievePassword}
              form={form}
              identifierNotExistedErrorHandler={identifierNotExistedErrorHandler}
            />
            <PasswordInputStep form={form} />
            <Button
              size="md"
              fullWidth
              loading={submitting}
              disabled={submitButtonDisabled}
              onClick={() => handlePasswordReset(form.values)}
            >
              {t('title')}
            </Button>
            <Footer align="left" />
          </Stack>
        </Stack>
      </form>
    </PasswordResetFormProvider>
  )
}

export default ResetForm
